import React, { useCallback, useEffect, useRef, ReactNode } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaCarouselType, EmblaEventType } from 'embla-carousel';

import * as styles from './CityCarousel.css';

const TWEEN_FACTOR_BASE = 0.2;

type CityCarouselProps = {
  options: Array<{ slide: ReactNode }>;
  controls?: (payload: {
    next: () => void;
    prev: () => void;
    slidesTotal: number;
  }) => void;
  updateArrows?: (payload: { beginning: boolean; end: boolean }) => void;
};

export const CityCarousel = ({
  options,
  controls,
  updateArrows
}: CityCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true
  });

  const tweenFactor = useRef(0);
  const tweenNodes = useRef<HTMLElement[]>([]);

  const controlsRef = useRef(controls);
  const updateArrowsRef = useRef(updateArrows);

  useEffect(() => {
    controlsRef.current = controls;
    updateArrowsRef.current = updateArrows;
  }, [controls, updateArrows]);

  const setTweenNodes = useCallback((api: EmblaCarouselType) => {
    tweenNodes.current = api.slideNodes().map((slideNode) => {
      return slideNode.querySelector('.embla__parallax__layer') as HTMLElement;
    });
  }, []);

  const setTweenFactor = useCallback((api: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * api.scrollSnapList().length;
  }, []);

  const tweenParallax = useCallback(
    (api: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = api.internalEngine();
      const scrollProgress = api.scrollProgress();
      const slidesInView = api.slidesInView();
      const isScrollEvent = eventName === 'scroll';

      api.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        if (!slidesInSnap) return;

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          let translate = diffToTarget * (-1 * tweenFactor.current) * 100;

          const MAX_TRANSLATE = 35;

          translate = Math.max(
            Math.min(translate, MAX_TRANSLATE),
            -MAX_TRANSLATE
          );

          let tweenNode = tweenNodes.current[slideIndex];
          if (!tweenNode) {
            const slideElement = api.slideNodes()[slideIndex];
            if (slideElement) {
              tweenNode = slideElement.querySelector(
                '.embla__parallax__layer'
              ) as HTMLElement;
              if (tweenNode) tweenNodes.current[slideIndex] = tweenNode;
            }
          }

          if (tweenNode) {
            tweenNode.style.transform = `translateX(${translate}%)`;
          }
        });
      });
    },
    []
  );

  const onSelect = useCallback((api: EmblaCarouselType) => {
    if (updateArrowsRef.current) {
      updateArrowsRef.current({
        beginning: !api.canScrollPrev(),
        end: !api.canScrollNext()
      });
    }
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    setTweenNodes(emblaApi);
    setTweenFactor(emblaApi);
    tweenParallax(emblaApi);
    onSelect(emblaApi);

    emblaApi
      .on('reInit', setTweenNodes)
      .on('reInit', setTweenFactor)
      .on('reInit', tweenParallax)
      .on('reInit', onSelect)
      .on('scroll', tweenParallax)
      .on('slideFocus', tweenParallax)
      .on('select', onSelect);

    return () => {
      emblaApi
        .off('reInit', setTweenNodes)
        .off('reInit', setTweenFactor)
        .off('reInit', tweenParallax)
        .off('reInit', onSelect)
        .off('scroll', tweenParallax)
        .off('slideFocus', tweenParallax)
        .off('select', onSelect);
    };
  }, [emblaApi, tweenParallax, setTweenNodes, setTweenFactor, onSelect]);

  useEffect(() => {
    if (!emblaApi || !controlsRef.current) return;
    controlsRef.current({
      next: () => emblaApi.scrollNext(),
      prev: () => emblaApi.scrollPrev(),
      slidesTotal: options.length
    });
  }, [emblaApi, options.length]);

  return (
    <div className={styles.CityCarouselWrapper}>
      <div className={styles.EmblaViewport} ref={emblaRef}>
        <div className={styles.EmblaContainer}>
          {options.map((item, index) => (
            <React.Fragment key={index}>{item.slide}</React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

interface CityCarouselSkeletonProps {
  count?: number;
}

export const CityCarouselSkeleton = ({
  count = 4
}: CityCarouselSkeletonProps) => {
  const skeletonItems = Array.from({ length: count });

  return (
    <div className={styles.CityCarouselWrapper}>
      <div className={styles.EmblaViewport}>
        <div className={styles.EmblaContainer}>
          {skeletonItems.map((_, index) => (
            <div key={index} className={styles.skeletonSlide}>
              <div className={styles.skeletonCard}></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
